<template>
  <div v-if="active && !loading" class="modal is-active">
    <div class="modal-background"></div>
    <aside class="modal-card is-flex" qa="personalize-modal">
      <span v-if="!isSigningUp && !isCreate" class="close cursor-pointer" @click="close"><i class="fal fa-times"></i></span>
      <!-- header when the user is not signed in; ie: clicks 'personalize feed' on section home -->
      <header v-if="!isCreate && user && isSigningUp" class="title">
        <h2 class="not-signed-up-header"> {{ $t('updateProfile.welcome')}} <span class="username">{{user.userName}}</span>! </h2>
        <p class="welcome-sub">{{ $t('updateProfile.beforeWeStart')}} <br> {{ $t('updateProfile.clickCategories')}}!</p>
      </header>
      <!-- header that appears during the sign up process -->
      <header v-if="!isCreate && user && !isSigningUp" class="title">
        <h2 class="signed-up-header"><span v-if="!$isProjectboard && !$isGirlsAndSteam">{{$t('modalCategories.text1')}}</span> <span v-if="$isProjectboard && !$isGirlsAndSteam">{{$t('modalCategories.text3')}}</span> <span class="mobile-line-break">{{$t('modalCategories.text2')}}</span></h2>
      </header>
      <header v-if="isCreate && $isYSC" class="title">
        <h2 class="signed-up-header">{{ $t('category.title') }}</h2>
      </header>
      <header v-if="isCreate && $isAjas" class="title">
        <h2 class="signed-up-header">{{ $t('category.titleAjas') }}</h2>
      </header>
      <section class="categories-container" :class="{'is-signingup' : user && isSigningUp, 'has-tooltips': isCreate}">
        <div v-for="(category, index) in categories" :key="category.id" class="category">
          <div class="is-flex a-category unselectable" :class="{'disabled': ($isYSC || $isAjas) && selectedCategories.length >=1 && !isCategoryStarred(category) && isCreate, 'a-category-ajas' : $isAjas}">
            <category-star v-if="isCategoryStarred(category)" :context="`user-select`"></category-star>
            <div v-if="!isCategoryStarred(category)" class="category-image-container is-flex"><img class="category-image" :src="category.image" :alt="`Category ${category.name} Makey Icon`"/></div>
            <span class="cursor-pointer is-flex" :disabled="selectedCategories.length === 1 && isCreate">
              <div class="category-title" :class="{'is-collapsed': !isCategoryExpanded(category)}" @click="showAllOption ? updateExpandCategory(category) : clickCategory(category, category)">
                {{ category.name.toUpperCase() }}
              </div>
              <span v-if="showAllOption" class="icon cursor-pointer" @click="updateExpandCategory(category)">
                <i v-if="!isCategoryExpanded(category)" class="fal fa-chevron-down"></i>
                <i v-if="isCategoryExpanded(category)" class="fal fa-chevron-up"></i>
              </span>
              <span v-if="$isYSC" class="icon cursor-pointer" :data-balloon="category.description" :data-balloon-pos="dataBalloonPos(index)" data-balloon-length="medium">
                <i class="tooltip-icon has-text-primary fas fa-info"></i>
              </span>
            </span>
          </div>
          <div v-if="isCategoryExpanded(category)" class="is-flex subcategories-container">
            <div class="subcategory cursor-pointer unselectable" :class="{'selected': isCategorySelected(subcategory)}" v-for="subcategory in category.subCategories" :key="subcategory.id" @click="clickCategory(subcategory, category)">
              {{ subcategory.name }}
            </div>
            <div v-if="!hasSubCategories(category) && showAllOption" class="subcategory cursor-pointer unselectable" :class="{'selected': isCategorySelected(category)}" @click="clickCategory(category, category)">
              All
            </div>
          </div>
        </div>
      </section>
      <footer class="is-flex button-container" :class="{'is-signingup' : user && isSigningUp}">
        <span class="is-flex is-pulled-left">
          <button class="button is-white cursor-pointer" @click="clearAllCategories" :disabled="selectedCategories.length === 0"> {{ $t('category.clear') }} </button>
          <button v-if="showAllOption" class="button is-white cursor-pointer" @click="updateAllExpanded"> {{allExpanded ? 'Collapse All' : 'Expand All'}} </button>
        </span>
        <button type="submit" class="button is-primary mr-5 gtm-login-signup" :class="{'is-ios-android': isIosOrAndroid}" @click="setCategories" qa="Apply-button" :disabled="(selectedCategories.length < minCategories)"> {{ $t('category.apply') }} </button>
      </footer>
      <div class="modal" :class="{'is-active': showLoadingModal}">
        <div class="modal-background"></div>
        <div class="modal-content">
          <button v-if="hasLoadingError" @click="forceClose" class="delete close-button" aria-label="close"></button>
          <span v-else class="loader"></span>
          <div class="text">{{ loadingMessage }}</div>
        </div>
      </div>
    </aside>
  </div>
</template>

<script>
import CategoryStar from '@/components/Controls/CategoryStar'

import breakpoints from '@/libs/breakpoints'
import userAgent from '@/libs/user-agent'

import auth from '@/mixins/auth'
import componentState from '@/mixins/component-state'

export default {
  data: function () {
    return {
      active: false,
      selectedCategories: [],
      starredCategories: [],
      loading: false,
      isSigningUp: false,
      categoriesChanged: false,
      redirectToHome: false,
      showLoadingModal: false,
      hasLoadingError: false,
      expandedCategories: [],
      displayingToolTip: false
    }
  },
  props: ['isNavBar', 'isCreate'],
  components: { CategoryStar },
  mixins: [componentState, auth],
  computed: {
    isIosOrAndroid: userAgent.isIOS || userAgent.isAndroid,
    isIOS: userAgent.isIOS,
    categories: function () {
      return this.$store.getters.categoriesLoaded ? this.$store.getters.sortedProjectCategories(true, true) : []
    },
    user: function () {
      return this.$store.getters.currentUser
    },
    loadingMessage: function () {
      return this.hasLoadingError ? 'Looks like something went wrong. Please close and try again' : 'Loading your categories...'
    },
    allExpanded: function () {
      return this.expandedCategories.length === this.categories.length
    },
    minCategories: function () {
      if (!this.$isProjectboard) {
        return this.isCreate ? 1 : 3
      }
    },
    isMobile: function () {
      return breakpoints.isMobile()
    },
    showAllOption: function () {
      return !this.$isYSC && !this.$isAjas
    }
  },
  watch: {
    active: function (active) {
      this.categoriesChanged = false
      if (active) {
        this.setSigningupValue()
        this.getUserCategories()
      }
      active ? this._callback('lockScrolling', false) : this._callback('unlockScrolling', true)
      if (this.$store.getters.modalIsVisible('category') && !this.$store.getters.modalIsVisible('login')) {
        this.isDispatched = true
      }
    },
    '$store.getters.currentPage': function () {
      this.$store.dispatch('getCategories')
    }
  },
  mounted: function () {
    this.getUserCategories()
    if (this.$route.params.fairSlug) {
      this.$store.dispatch('setPage', this.$route.params.fairSlug)
    } else if (this.$route.query.page) {
      this.$store.dispatch('setPage', this.$route.query.page)
    } else if ((this.$isYSC || this.$isOasam) && this.$store.getters.currentPage === '') {
      this.$store.dispatch('setPage', '_latest')
    }
    if (this.categories.length === 0 && this.$store.getters.currentPage !== '') {
      this.$store.dispatch('getCategories')
    }
  },
  methods: {
    refreshCategoryModal: function () {
      if (this.active && !this.signupSource) {
        // short circuit this modal if it's already completed (on refresh)
        if (this.selectedCategories.length >= 3) {
          this.$nextTick(() => {
            this.close()
          })
        }
      }
    },
    showModal: function (source) {
      this.active = true
      this.signupSource = source
      this.$store.dispatch('isSigningUp', true)
    },
    getUserCategories: function () {
      this.selectedCategories = []
      if (this.isCreate) {
        this.clearAllCategories()
        return
      }

      if (this.isLoggedIn && this.active) {
        this.loading = true
        var timeout = setTimeout(() => {
          this.showLoadingModal = true
        }, 1000)
        this.$store.dispatch('getUserCategories', this.user.userName).then((categories) => {
          clearTimeout(timeout)
          this.showLoadingModal = false
          categories.forEach((category) => {
            const parentID = category.parentID > 0 ? category.parentID : category.id
            this.selectedCategories.push(category.id)
            if (this.starredCategories.indexOf(parentID) === -1) {
              this.starredCategories.push(parentID)
            }
            if (this.expandedCategories.indexOf(parentID) === -1) {
              this.expandedCategories.push(parentID)
            }
          })
          this.refreshCategoryModal()
        }).catch(() => {
          this.hasLoadingError = true
        }).finally(() => {
          this.loading = false
        })
      }
    },
    setSigningupValue: function () {
      if (!this.signupSource) {
        const data = this.$store.getters.modalData('category')
        if (data) {
          this.signupSource = data.source
        }
      }
      this.isSigningUp = !this.isLoggedIn
    },
    setCategories: function () {
      if (this.isCreate) {
        this.$emit('updateCategories', this.selectedCategories)
        this.close()
        this.clearAllCategories()
        return
      }
      const modalData = this.$store.getters.modalData('category')
      if (modalData && modalData.source === 'storyFooter') {
        this.redirectToHome = true
      }
      const categories = this.selectedCategories

      if (this.isLoggedIn) {
        this.$store.dispatch('setUserCategories', { username: this.user.userName, categories }).then((response) => {
          this.categoriesChanged = true
          this.done()
          if (this.redirectToHome) {
            this.$router.push({ name: 'home', query: { r: this.refreshKey() } })
          }
        }, (error) => {
          this.error = error.message
        })
      } else {
        this.$store.dispatch('showLoginModal', {
          source: this.signupSource,
          selectedCategories: true,
          callback: (success) => {
            if (success && this.isLoggedIn) {
              this.close()
              this.$store.dispatch('setUserCategories', { username: this.user.userName, categories }).then((response) => {
                this.$emit('categoriesChanged', true)
              }, (error) => {
                this.error = error.message
              })
              if (this.redirectToHome) {
                this.$router.push({ name: 'home', query: { r: this.refreshKey() } })
              }
            } else {
              this.close()
            }
          }
        })
      }
    },
    selectedCategoryIndex: function (category) {
      return this.selectedCategories.indexOf(category.id)
    },
    isCategorySelected: function (category) {
      return this.selectedCategoryIndex(category) !== -1
    },
    expandedCategoryIndex: function (category) {
      return this.expandedCategories.indexOf(category.id)
    },
    isCategoryExpanded: function (category) {
      return this.expandedCategoryIndex(category) !== -1
    },
    starredCategoryIndex: function (category) {
      return this.starredCategories.indexOf(category.id)
    },
    isCategoryStarred: function (category) {
      return this.starredCategories.indexOf(category.id) !== -1
    },
    hasSubCategories: function (category) {
      return category.subCategories && category.subCategories.length > 0
    },
    clickCategory: function (category, parentCategory) {
      if (this.isCategorySelected(category)) {
        this.selectedCategories.splice(this.selectedCategoryIndex(category), 1)
        let oneSelected = false
        if (this.hasSubCategories(parentCategory)) {
          parentCategory.subCategories.forEach((subCat) => {
            if (this.isCategorySelected(subCat)) {
              oneSelected = true
            }
          })
        }
        if (this.isCategoryStarred(parentCategory) && !oneSelected) {
          this.starredCategories.splice(this.starredCategoryIndex(parentCategory), 1)
        }
      } else {
        if ((this.$isAjas || this.$isYSC) && this.isCreate) {
          this.selectedCategories = []
          this.starredCategories = []
        }
        this.selectedCategories.push(category.id)
        if (!this.isCategoryStarred(parentCategory)) {
          this.starredCategories.push(parentCategory.id)
        }
      }
    },
    updateExpandCategory: function (category) {
      if (!this.isCategoryExpanded(category)) {
        this.expandedCategories.push(category.id)
      } else {
        this.expandedCategories.splice(this.expandedCategoryIndex(category), 1)
      }
    },
    clearAllCategories: function () {
      this.selectedCategories = []
      this.starredCategories = []
    },
    updateAllExpanded: function () {
      if (this.allExpanded) {
        this.expandedCategories = []
      } else {
        this.categories.forEach((category) => {
          if (!this.isCategoryExpanded(category)) {
            this.expandedCategories.push(category.id)
          }
        })
      }
    },
    done: function () {
      this.close()
      this.$emit('categoriesChanged', this.categoriesChanged)
      this.selectedCategories = []
    },
    refreshKey: function () {
      return (Math.random() + 1).toString(36).substring(7)
    },
    close: function () {
      this.active = false
      this.$emit('modalDone', true, true)
      this.$store.dispatch('isSigningUp', false)
    },
    forceClose: function () {
      this.close()
    },
    dataBalloonPos: function (index) {
      let pos = 'up'
      if (index < this.categories.length / 2) {
        pos = 'down'
      }
      if (this.isMobile) {
        pos = pos + '-right'
      }
      return pos
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';

  $border-color: #DCDCDC;
  $modal-body-color: #F2F2F2;

  .close {
    font-size: 1.3rem;
    background-color: transparent;
    opacity: 0.75;
    position: absolute;
    right: 1rem;
    top: 0.5rem;
    border: none;
  }

  .ysc-modal-card {
    background: white !important;
    max-height: 700px !important;
    max-width: 640px !important;
    overflow-x: hidden;
    @include mobile-and-ipad {
      overflow: visible;
    }
  }

  .modal-card {
    overflow-y: hidden;
    overflow: auto;
    background: $modal-body-color;
    flex-direction: column;
    justify-content: space-between;
    max-width: 620px;
    width: 98vw;
    max-height: 640px;
    height: calc(100vh - 20px);
    @include mobile {
      max-height: 80%;
    }
    img {
      display: block;
    }
  }

  .category-image-container {
    min-width: 26px;
    min-height: 26px;
  }
  .category-image {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    @include shadow;
  }

  // HEADER styles
  .title {
    border-bottom: 1px solid $border-color;
    margin-bottom: 0 !important;
    text-align: center;
    h2 {
      font-family: $primary-font;
      font-weight: $primary-font-weight;
      font-size: 1.4rem;
    }
    .signed-up-header {
      padding: 1.8rem 0 1.2rem;
      @include mobile {
        font-size: 1.2rem;
        padding: 1.5rem 0 1rem;
        .mobile-line-break {
          display: block;
          padding-top: 2px;
        }
      }
    }
    .not-signed-up-header {
      padding: 1rem 0 0.3rem;
      .username {
        font-weight: 700;
      }
    }
    .welcome-sub {
      font-size: 0.9rem;
      font-weight: 300;
      padding: 0 1.5rem 0.9rem;
      @include mobile {
        font-size: 0.8rem;
      }
    }
  }

  .loader {
    display: block;
    font-size: 2rem;
    margin: 1rem auto;
  }

  .categories-container {
    overflow-y: scroll;
    overflow-x: hidden;
    margin-top: 1rem;
    &.has-tooltips {
      overflow: visible
    }
  }

  .category {
    width: 72.5%;
    margin: auto;
    margin-bottom: 1rem;
    @include mobile {
      width: 80%;
    }
  }

  .a-category {
    margin-top: 1.25rem;
  }

  .a-category-ajas {
    margin-top: 0.35rem;
  }

  .category-title {
    font-weight: 700;
    margin: 0 0 1.25rem 0.5rem;
    &.is-collapsed {
      margin-bottom: 0.75rem;
    }
  }

  .subcategories-container {
    margin-left: 2rem;
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .subcategory{
    display: flex;
    justify-content: center;
    align-content: center;
    flex-direction: column;
    font-size: 0.8rem;
    background-color: white;
    margin-right: 0.5rem;
    margin-bottom: 0.5rem;
    padding: 0.25rem 0.75rem;
    border: 1px solid transparent;
    &.selected {
      background-color: $modal-body-color;
      border-color: black;
    }
  }

  .select-all {
    text-decoration: underline;
    margin-left: 0.5rem;
    font-size: 0.91rem;
  }

  // FOOTER styles
  .button-container {
    width: 100%;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 0;
    background-color: white;
    border: 1px solid darkgrey;
    border-style: none;
    border-top-style: solid;
    @include mobile {
      padding-left: 1rem;
      min-height: 50px;
    }
  }
  .button {
    border-radius: 0;
    height: auto;
    padding: 0.5rem 1.4rem;
    font-size: 0.85rem;
    background: $primary;
    border: 0;
    color: #fff;
    text-transform: uppercase;
    @include mobile {
      padding: 0.5rem 0.75rem;
    }
  }
  .loader {
    display: block;
    font-size: 2rem;
    margin: 1rem auto;
  }
  .tooltip-icon {
    font-size: 0.75rem;
  }

  //Disable Styles
  .disabled {
    cursor: not-allowed;
    border: none !important;
    opacity: .2;
    z-index: 1;
  }
</style>
